import { useEffect, useState } from 'react';
import type { CreateLicensePayload, LicenseDetail, LicenseType } from '../api';
import { useCriarLicense, useAtualizarLicense, useAtivos } from '../hooks';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { LICENSE_TYPE_LABELS } from '../utils/formatters';

interface Props {
  license?: LicenseDetail | null;
  onCancel: () => void;
  onSuccess: (license: LicenseDetail) => void;
}

/**
 * Formulário de licença — componente puro renderizado dentro de uma
 * página (LicencaNovaPage / LicencaEditarPage), não mais em modal.
 *
 * Coerência com AtivoForm: mesmo tratamento (página cheia, breadcrumb no
 * container pai, botões Salvar/Cancelar embaixo). Substitui a versão
 * antiga em Dialog do Headless UI.
 */
export function LicencaForm({ license, onCancel, onSuccess }: Props) {
  const isEdit = !!license;
  const criar = useCriarLicense();
  const atualizar = useAtualizarLicense(license?.id ?? '');
  const busy = criar.isPending || atualizar.isPending;

  const [form, setForm] = useState<CreateLicensePayload>({
    name: '', vendor: '', licenseType: 'microsoft', totalSeats: 1, contractedAt: '',
  });
  const [error, setError] = useState<string | null>(null);

  const ativosQuery = useAtivos({ limit: 500 });
  const ativoOptions = (ativosQuery.data?.data ?? []).map((a) => ({
    value: a.id,
    label: `${a.displayCode ?? a.assetCode} — ${a.description}`,
  }));

  const computedTotalCost =
    form.costPerSeat != null && form.totalSeats > 0
      ? parseFloat((form.totalSeats * form.costPerSeat).toFixed(2))
      : undefined;

  const formattedTotalCost =
    computedTotalCost != null
      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(computedTotalCost)
      : '';

  useEffect(() => {
    if (license) {
      setForm({
        name: license.name,
        vendor: license.vendor,
        licenseType: license.licenseType,
        totalSeats: license.totalSeats,
        contractedAt: license.contractedAt,
        expiresAt: license.expiresAt ?? undefined,
        assetId: license.asset?.id ?? undefined,
        costPerSeat: license.costPerSeat != null ? Number(license.costPerSeat) : undefined,
        notes: license.notes ?? undefined,
      });
    }
    setError(null);
  }, [license]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const payload = { ...form };
      if (isEdit) {
        const result = await atualizar.mutateAsync(payload);
        onSuccess(result.license);
      } else {
        const result = await criar.mutateAsync(payload);
        onSuccess(result.license);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao salvar licença'));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Identificação</h3>
        <div className="space-y-3">
          <TextInput
            label="Nome *"
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextInput
              label="Fornecedor *"
              required
              minLength={2}
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            />
            <AutocompleteSelect
              label="Tipo *"
              options={(Object.entries(LICENSE_TYPE_LABELS) as Array<[LicenseType, string]>).map(([v, l]) => ({ value: v, label: l }))}
              value={form.licenseType}
              onChange={(v) => setForm({ ...form, licenseType: v as LicenseType })}
            />
          </div>
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Contrato</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <TextInput
            label="Seats contratados *"
            required
            type="number"
            min={1}
            value={form.totalSeats}
            onChange={(e) => setForm({ ...form, totalSeats: Number(e.target.value) })}
          />
          <TextInput
            label="Contratada em *"
            required
            type="date"
            value={form.contractedAt}
            onChange={(e) => setForm({ ...form, contractedAt: e.target.value })}
          />
          <TextInput
            label="Vence em"
            type="date"
            value={form.expiresAt ?? ''}
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value || undefined })}
          />
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label="Custo por seat"
            type="number"
            step="0.01"
            min={0}
            value={form.costPerSeat ?? ''}
            onChange={(e) => setForm({ ...form, costPerSeat: e.target.value ? Number(e.target.value) : null })}
          />
          <TextInput
            label="Custo total"
            type="text"
            readOnly
            value={formattedTotalCost}
            onChange={() => {}}
          />
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Vínculos e observações</h3>
        <div className="space-y-3">
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">
              Ativo vinculado (opcional)
            </span>
            <AutocompleteSelect
              label="Ativo (por código ou descrição)"
              options={ativoOptions}
              value={form.assetId ?? ''}
              onChange={(v) => setForm({ ...form, assetId: v || undefined })}
            />
            <p className="mt-1 text-xs text-gray-500">
              Use quando a licença pertence a um hardware específico — ex.: Windows
              OEM de um notebook, chave de um servidor. Deixe vazio para licenças
              atribuídas por seats a pessoas.
            </p>
          </div>
          <Textarea
            label="Observações"
            rows={3}
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || undefined })}
          />
        </div>
      </section>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={busy}>
          {busy ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Cadastrar licença'}
        </Button>
      </div>
    </form>
  );
}
